import { createFileRoute, useNavigate } from '@tanstack/react-router';
import * as z from 'zod';
import { DELIVERY_STATUS_TABS } from '@/entities/delivery-order';
import { useLegacyMode } from '@/entities/legacy-mode';
import { LEGACY_STATUS_TAB_IDS } from '@/entities/legacy-order';
import {
  parseWireDate,
  formatWireDate,
  resolvePreset,
  startOfDay,
} from '@/entities/legacy-date-range';
import { DeliveryOrdersPage } from '@/pages/delivery-orders';
import { LegacyOrdersPage, readStoredLegacyPageSize } from '@/pages/legacy-orders';
import { requireAuth } from '../require-auth';

/**
 * URL state for the Доставка screen. The URL is the single source of truth:
 * `status` (the active filter pill), `search` (free text), and `from`/`to`
 * (the week range) scope the list. All optional/defaulted.
 *
 * The schema serves BOTH the new screen and the legacy one, whose status
 * vocabularies differ (`all`, `active`, … vs the old app's `ALL`,
 * `SHCHEDULED`, …). It accepts either set, and each page falls back to its own
 * default for a value it doesn't recognize — so toggling legacy mode
 * mid-session can never strand the route on an unparseable search param.
 */
const deliveryOrdersSearchSchema = z.object({
  // Defaulted as well as `.catch()`ed: `.catch()` alone recovers a BAD value
  // but still makes the param required at every `navigate({to})` call site.
  status: z
    .enum([...DELIVERY_STATUS_TABS, ...LEGACY_STATUS_TAB_IDS])
    .default('all')
    .catch('all'),
  search: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  /**
   * Legacy-only: 1-based page index for the old grid's pager. Defaulted (not
   * just `.catch()`ed) so `navigate({to: '/delivery/orders'})` from elsewhere
   * doesn't have to supply it — `.catch()` alone still makes the param
   * *required* at the call site.
   */
  page: z.number().int().min(1).default(1).catch(1),
  /**
   * Legacy-only: rows per page for the old grid's "Показать по N" dropdown.
   * Defaulted (same reasoning as `page`) — and the default is a FUNCTION so
   * an absent param seeds from the stored table preference
   * (`table_offset_sizes.delivery`, the old panel's own key) rather than a
   * hardcoded 15, reproducing how the old grid remembered its size.
   */
  limit: z
    .number()
    .int()
    .min(1)
    .default(() => readStoredLegacyPageSize())
    .catch(() => readStoredLegacyPageSize()),
});

export const Route = createFileRoute('/delivery/orders')({
  // Auth guard — see `require-auth.ts`. Runs before the component mounts,
  // so no query fires without a session.
  beforeLoad: requireAuth,
  validateSearch: deliveryOrdersSearchSchema,
  component: DeliveryOrdersRoute,
});

/** The new screen's status union — the prop type `DeliveryOrdersPage` accepts. */
type NewStatus = (typeof DELIVERY_STATUS_TABS)[number];

/**
 * Type guards rather than bare `Set.has`, so each page receives its own
 * narrowed status union instead of the widened `string` the shared search
 * schema produces.
 */
const isNewStatus = (s: string): s is NewStatus =>
  (DELIVERY_STATUS_TABS as readonly string[]).includes(s);

const isLegacyStatus = (s: string): boolean =>
  (LEGACY_STATUS_TAB_IDS as readonly string[]).includes(s);

/**
 * The legacy window from `from`/`to`, defaulting to the current WEEK when
 * either is absent or malformed. A one-sided range is treated as no range at
 * all rather than half-applied, which would silently query a window the
 * merchant never chose.
 *
 * A week, not "today": with no params the old page QUERIED
 * `startOf("week")..tomorrow` (`index.tsx:69-76`) — whatever its picker's
 * label showed — so the default window must be the Monday-based week for the
 * list to hold the same rows. (`startOfWeek` is Monday-based, verified
 * against the live panel.) The page adds the exclusive +1 day to `to`
 * itself.
 */
function readLegacyRange(from: string | undefined, to: string | undefined) {
  const parsedFrom = parseWireDate(from);
  const parsedTo = parseWireDate(to);
  if (!parsedFrom || !parsedTo) return resolvePreset('week');
  // Tolerate a reversed range from a hand-edited URL.
  return parsedFrom <= parsedTo
    ? { from: parsedFrom, to: parsedTo }
    : { from: parsedTo, to: parsedFrom };
}

function DeliveryOrdersRoute() {
  const { status, search, page, limit, from, to } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const legacy = useLegacyMode();

  if (legacy) {
    return (
      <LegacyOrdersPage
        // A status carried over from the new UI (`all`, `active`, …) means
        // nothing to the legacy list, so fall back to its own "all" pill.
        status={isLegacyStatus(status) ? status : 'ALL'}
        search={search ?? ''}
        page={page}
        limit={limit}
        range={readLegacyRange(from, to)}
        onRangeChange={(next) => {
          void navigate({
            search: (prev) => ({
              ...prev,
              from: formatWireDate(startOfDay(next.from)),
              to: formatWireDate(startOfDay(next.to)),
            }),
          });
        }}
        onStatusChange={(next) => {
          void navigate({ search: (prev) => ({ ...prev, status: next }) });
        }}
        onSearchChange={(next) => {
          void navigate({
            search: (prev) => ({ ...prev, search: next || undefined }),
          });
        }}
        onPageChange={(next) => {
          void navigate({ search: (prev) => ({ ...prev, page: next }) });
        }}
        onLimitChange={(next) => {
          // A new page size restarts paging — page 4 of 15-per-page points
          // nowhere once the pages are 55 rows tall.
          void navigate({ search: (prev) => ({ ...prev, limit: next, page: 1 }) });
        }}
      />
    );
  }

  return (
    <DeliveryOrdersPage
      status={isNewStatus(status) ? status : 'all'}
      search={search}
      onStatusChange={(next) => {
        void navigate({ search: (prev) => ({ ...prev, status: next }) });
      }}
    />
  );
}
